// /series splash — the AI-podcast-generator landing page: hero, how-it-works, the six-card fold, the live featured directory, eight anchored feature sections, the cross-page seams, and one closing CTA.
// Loader-only ⇒ pure SSG: fetched once at build, baked into the static HTML. No clientLoader/HydrateFallback (those bake a spinner + re-fetch live on hydrate, defeating SSG/SEO).
// Node-prerender-safe: fetch + MUI + local helpers only — no store/Amplify/@shared imports (this module's whole graph is eagerly imported during Node prerender, wave-1's gotcha). `src/shared/config/credits` is import-free consts, so it is safe here and is the only place a dollar figure may come from.
// Chrome (theme + nav + footer) comes from splash-chrome, which every marketing page shares; it provides the ThemeProvider, so this file must NOT also wrap `Mui`.
import { useState, type ReactNode } from 'react';
import { href, Link as RouterLink } from 'react-router';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { ExplainerVideo } from '../../src/ui/Components/ExplainerVideo';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import InsightsIcon from '@mui/icons-material/InsightsOutlined';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
// Icon choice is a COPY decision, not just a visual one: MUI bakes the component name into a `data-testid` that survives the production build, so `AutoStories` would leak the literal string "Stories" into the prerendered HTML — an A13 violation ("Series", never "Stories") that no typecheck or source review can see.
import HubIcon from '@mui/icons-material/HubOutlined';
import PodcastsIcon from '@mui/icons-material/PodcastsOutlined';
import { SplashChrome, AUTH_SIGN_UP } from './splash-chrome';
import { Anchor, Band, SectionHeader, FeatureCard, CtaBand, type FeatureTone } from './splash-sections';
import { MigrationNotice } from './series-migration';
import { FeatureCards, FeatureSections, FEATURE_LIST } from './series-features';
import { fetchFeaturedShows, type FeaturedShow } from './series-data';
import { metaDescription, splashMeta, softwareApplicationLd, ORIGIN } from './series-ld';
import { CREDIT_TOPUP_DISPLAY, CHAPTER_COST_DISPLAY } from '../../../../shared/config/credits';
import { UseCaseCards, UseCaseSection } from './series-usecases';
import SeriesDemo from './series-demo';
import FlagshipProof from './series-proof';
import type { Route } from './+types/series';

const TITLE = 'AI Podcast Generator for Stories and Audio Courses | Gnothi';
const DESCRIPTION = metaDescription(
  'Create a continuing story around what you want to learn, or make a direct audio course. Gnothi writes each chapter and includes narration.',
);
const PATH = '/series';

export async function loader(): Promise<{ featured: FeaturedShow[] }> {
  // Fail-soft is deliberate (atom #5) but must not be silent: this runs at PRERENDER, so a transient artifact fetch failure bakes an empty splash into the deploy and the build still goes green. The build log is the only place anyone could ever see it.
  try { return { featured: await fetchFeaturedShows() }; }
  catch (e) { console.warn('[series] featured fetch failed — rendering an empty directory', e); return { featured: [] }; }
}

export const meta: Route.MetaFunction = () => [
  ...splashMeta({ title: TITLE, description: DESCRIPTION, path: PATH }),
  {
    // The LD `url` deep-links the generator section while og:url/canonical stay bare `/series` — that asymmetry is deliberate, and it is why `splashMeta` does not own this descriptor.
    'script:ld+json': softwareApplicationLd({
      name: 'Gnothi Series',
      description: DESCRIPTION,
      url: `${ORIGIN}${PATH}#podcast-generator`,
      applicationCategory: 'MultimediaApplication',
      featureList: FEATURE_LIST,
    }),
  },
];

// Step 1's example is fiction's `cooking` Lessons chip, step 2's "a sentence or two" is fiction's `premiseHelp` ("One or two sentences."), both shared/utils/seriesCreateCopy.ts; step 3's length is the home hero's (MINUTES_PER_CHAPTER, ~31-minute flagship releases).
const STEPS = [
  'Choose an audio course, a story for its own sake, or a story with lessons in it, like a fantasy cook teaching you one real technique at a time.',
  'Write the premise in a sentence or two, then add the lessons or topics to follow across chapters.',
  'Create the first chapter, about half an hour long, then read, listen, and decide what comes next.',
];

// `href` for a target on THIS page, `to` for another route — never `to` for the in-page case: a `<RouterLink to="#campaign">` resolves against `/series` and bakes `href="/series#campaign"`, which the DOM gate rejects because it navigates the whole page instead of scrolling.
const SEAMS: { to?: string; href?: string; icon: ReactNode; tone: FeatureTone; title: string; body: string }[] = [
  { href: '#campaign', icon: <HubIcon />, tone: 'prompt', title: 'Build a campaign chronicle', body: 'Turn a tabletop campaign into a fiction Series: point it at your session notes and get a narrated chapter after every game.' },
  { to: '/publishing', icon: <PodcastsIcon />, tone: 'themes', title: 'Publish it as a podcast', body: 'Turn on the RSS feed, choose who can find the show, follow it in a podcast app, or submit it to directories.' },
  // Include Fields is its own per-Series switch, independent of journal scope (services/routes/stories/generate.ts `include_fields`); the benefit is the Check-in segment's own description (src/shared/authorship/prompts/presets/segments/check-in.mdx).
  { to: '/fields', icon: <InsightsIcon />, tone: 'behavior', title: 'Chapters that check in on your habits', body: 'Include your Fields in a Series and add the Check-in segment. Chapters ask how a project is going, name the next step, and offer a tip when a habit slips.' },
  { to: '/community', icon: <GroupsIcon />, tone: 'themes', title: 'Choose shared context', body: 'Selected shared entries can contribute another person’s context when your Series source settings include them.' },
  { to: '/', icon: <HubIcon />, tone: 'prompt', title: 'Use one connected workspace', body: 'Series, notes, fields, chat, and groups stay available together, while each Series can work without journal context.' },
];

function Hero() {
  return (
    <Band color="dark" hero>
      <Grid container spacing={{xs: 3, md: 5}} sx={{alignItems: 'center'}}>
        <Grid size={{xs: 12, md: 5}}>
          <Stack spacing={2} sx={{alignItems: 'flex-start'}}>
            <Typography variant="overline" component="p" sx={{color: 'splash.accentOnDark', fontWeight: 800}}>AI podcast generator for stories and audio courses</Typography>
            <Typography variant="h2" component="h1" sx={{fontWeight: 800, fontSize: {xs: '2rem', sm: '2.5rem', md: '3rem'}}}>Create a story around what you want to learn.</Typography>
            {/* Same deck and citations as the home hero (home.tsx). */}
            <Typography component="p" sx={{fontSize: '1.1rem', opacity: 0.86, display: {xs: 'none', sm: 'block'}}}>Choose a world and what you want to learn. Gnothi writes and narrates a continuing series that teaches it through the story, like a fantasy cook passing on one real technique at a time. Chapters run about half an hour, and each picks up where the last left off. Prefer a direct lesson? Make an audio course.</Typography>
            <Typography component="p" sx={{fontSize: '1rem', opacity: 0.86, display: {xs: 'block', sm: 'none'}}}>Choose a world and a subject. Gnothi writes and narrates the series.</Typography>
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} sx={{width: {xs: '100%', sm: 'auto'}}}>
              <Button component={RouterLink} to={AUTH_SIGN_UP} variant="contained" size="large">Create your series</Button>
              <Button component="a" href="#flagship" variant="outlined" color="inherit" size="large">Hear an example</Button>
            </Stack>
          </Stack>
        </Grid>
        <Grid size={{xs: 12, md: 7}}><SeriesDemo /></Grid>
      </Grid>
    </Band>
  );
}

function HowItWorks() {
  return (
    <Anchor sx={{ mb: { xs: 6, md: 8 } }}>
      <SectionHeader title="How it works" />
      <ExplainerVideo name="series" sx={{ width: '100%', maxWidth: 720, mx: 'auto', mb: { xs: 4, md: 5 } }} />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 4, md: 5 }} sx={{ justifyContent: 'center', alignItems: 'flex-start' }}>
        {STEPS.map((text, i) => (
          <Stack key={i} spacing={1.5} sx={{ maxWidth: 300, alignItems: 'center' }}>
            {/* The accent FILL pair, not `primary.main`/`primary.contrastText`: white on `#00A8E8` is 2.7:1, under even the 3:1 large-text threshold this 1.25rem/700 numeral qualifies for. */}
            <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: 'splash.accent', color: 'splash.onAccent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.25rem' }}>
              {i + 1}
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>{text}</Typography>
          </Stack>
        ))}
      </Stack>
    </Anchor>
  );
}

function ShowCard({ row }: { row: FeaturedShow }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
      <CardActionArea component={RouterLink} to={href("/series/:seriesid", { seriesid: row.slug })} sx={{ height: '100%', p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          {/* `loading="lazy"` is not a nicety here: these are the raw podcast covers (Apple's 1400px floor — one measured 3.26 MB) painted into a 64px slot, and `#featured` sits several folds down, so without it the prerendered HTML kicks off up to `limit` multi-MB fetches at parse time against the hero's own paint. No CLS risk to trade away — the box is fixed 64×64 below. */}
          <Box component="img" src={row.cover_url} alt={row.title} loading="lazy"
            sx={{ width: 64, height: 64, borderRadius: 1, objectFit: 'cover', flexShrink: 0, bgcolor: 'action.hover' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600 }} noWrap>{row.title}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>{row.owner_name}</Typography>
            <Typography variant="caption" color="text.secondary">{row.episode_count} episode{row.episode_count === 1 ? '' : 's'}</Typography>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

// The whole featured list is baked into the loader data, but only the first row of cards paints until asked: `expanded` starts false on BOTH prerender and hydrate, so the folded HTML matches — no mismatch — and the rest reveal client-side with zero refetch. Covers stay `loading="lazy"`, so an expand kicks off no fetch storm.
const FEATURED_FOLD = 12;

// Fail-soft (goal atom #5): an unpublished or unreachable directory artifact must never break the page. The SECTION and its `#featured` id render unconditionally, because the hero's secondary CTA and §3 both scroll-target it and a missing anchor is its own defect — only the grid is conditional.
// `id="featured"` is FROZEN, same footing as `#tts` in series-features.tsx. Three in-repo targeters, and the third is the one a redirect-table sweep misses: ocdevel's `/moremlg` and `/moreepisodes` vanity redirects (`src/ocdevel/app/lib/redirects.ts`); the breadcrumb on both /series/* detail pages; and the in-body `moremlg` ad (`src/ocdevel/app/routes/podcast/$id/ads.tsx`), which builds the absolute url itself and never touches the redirect table. Renaming it silently dead-ends URLs spoken aloud in shipped podcast ads.
function FeaturedShows({ rows }: { rows: FeaturedShow[] }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, FEATURED_FOLD);
  return (
    <Anchor id="featured">
      <SectionHeader title="Featured Series" deck="Invented worlds and practical courses, published as podcasts by Gnothi users." />
      {rows.length ? (
        <>
          <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
            {visible.map((row) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={row.id}>
                <ShowCard row={row} />
              </Grid>
            ))}
          </Grid>
          {rows.length > FEATURED_FOLD && (
            <Stack sx={{ alignItems: 'center', mt: 4 }}>
              <Button variant="outlined" onClick={() => setExpanded((e) => !e)}>
                {expanded ? 'Show fewer' : `Show all ${rows.length} series`}
              </Button>
            </Stack>
          )}
        </>
      ) : (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>The directory is refreshing. Check back in a moment.</Typography>
      )}
    </Anchor>
  );
}

function Seams() {
  return (
    <Anchor id="connects">
      <SectionHeader title="Where Series connects" deck="A Series can stand alone or use the notes, Fields, and shared context you choose." />
      <Grid container spacing={2}>
        {SEAMS.map(({ to, href, icon, tone, title, body }) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={to ?? href}>
            <FeatureCard title={title} icon={icon} tone={tone} to={to} href={href}>{body}</FeatureCard>
          </Grid>
        ))}
      </Grid>
    </Anchor>
  );
}

export default function SeriesIndex({ loaderData }: Route.ComponentProps) {
  return (
    <SplashChrome active="series">
      <Hero />
      {/* Below the hero, not above it: `MigrationNotice` decides its state in a useEffect, so it renders NOTHING at prerender and then mounts — above the H1 that shifted the flagship hero down on every single visit. `Container` is legal here only because this strip sits outside every `Band` (`Section` owns the padding inside one). A `?migrated=podcaster` arrival is still one flick from the open Alert. */}
      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 } }}><MigrationNotice /></Container>
      <Band color="light"><FlagshipProof /></Band>
      {/* One band per use-case rather than all three in one: stacked identical section+two-panel layouts read as a wall, and the alternation gives each its own page. The run ends on `light`, so the existing `grey` band below still alternates and no band under it had to be recolored. */}
      <Band color="grey"><UseCaseCards inPage /></Band>
      <Band color="light"><UseCaseSection index={0} /></Band>
      <Band color="grey"><UseCaseSection index={1} /></Band>
      <Band color="light"><UseCaseSection index={2} /></Band>
      <Band color="grey">
        <HowItWorks />
        <Anchor id="features">
          <SectionHeader title="Everything a Series does" deck="Choose a capability to jump to its details. Keep scrolling for reusable audio rolls and the reader and player." />
          <FeatureCards />
        </Anchor>
      </Band>
      <Band color="light"><FeaturedShows rows={loaderData.featured} /></Band>
      <Band color="grey"><FeatureSections group="The production loop" /></Band>
      <Band color="light"><FeatureSections group="What it writes from" /></Band>
      <Band color="grey"><FeatureSections group="Where it goes" /></Band>
      {/* The page's ONE accent band (hero + at most two are allowed, and it spent only the hero): the "it is all one product" statement earns the crescendo before the close. Dark-safe, checked in the emitted CSS: `Seams` carries no `text.primary` span, and `FeatureCard`'s Paper is an opaque `background.paper` even on the navy wash, so its own `text.primary` body stays on paper. */}
      <Band color="dark"><Seams /></Band>
      <CtaBand color="light"
        title="Create your series."
        deck={`You don't know how much you'll use it, so you shouldn't pay monthly for it. A chapter runs about ${CHAPTER_COST_DISPLAY} in credits, narration included, and a new account starts with enough for about four. More come in a one-time ${CREDIT_TOPUP_DISPLAY} top-up, and credits don't expire.`}
      />
    </SplashChrome>
  );
}
